import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Typography,
  Tooltip,
  Modal,
  Grid,
  Card,
  CardContent,
  Button,
  Input,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import FileSaveIcon from '../../../../../static/assets/svg/file_save.svg';
import ContentCopyIcon from '../../../../../static/assets/svg/content_copy.svg';
import CalendarIcon from '../../../../../static/assets/svg/calendar_month.svg';
import styles from './LibraryList.module.scss';
import { Helpers } from '../../../../common/helpers/helpers';
import { v4 as uuidv4 } from 'uuid';
import { LibraryListProps, LibraryItem } from './types';
import { useAppSelector } from '../../../../common/hooks/useAppSelector';
import { selectAllDocuments } from '../../../../slices/documents/documentsSlice';
import { selectAllCompanies } from '../../../../slices/companies/companiesSlice';
import { useLibraryFunctions } from '../../hooks/useLibraryFunctions';
import MessageModal from '../../../../common/message-modal/MessageModal';
import { ROUTES } from '../../../../common/constants/routes';
import { useNavigate } from 'react-router';
import FileTypeDownloadModal from '../../../../common/file-type-download-modal/FileTypeDownloadModal';
import { useFileTypeDownload } from '../../../../common/file-type-download-modal/useFileTypeDownload';
import { ProgressOverlay } from '../../../../common/progress-overlay/ProgressOverlay';
import MenuBlock from '../../../../common/menu-block';
import VerticalDotsIcon from '../../../../../static/assets/svg/more_vert.svg';
import { useDeleteLibraryItemMessageModal } from '../../hooks/useDeleteMessageModal';
import ContractGenerator from '../../../../common/contract-generator/ContractGenerator';
import { PacmanLoaderBlock } from '../../../../common/pacman-loader';
import { useContractsGenerator } from '../../../../common/contract-generator/hooks/useContractsGenerator';
import ContractGeneratorIcon from '../../../../../static/assets/svg/contract-generator-icon.svg';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import {
  selectDisclaimerChecked,
  acceptDisclaimer,
} from '../../../../slices/disclaimer/disclaimerSlice';
import { useAppDispatch } from '../../../../common/hooks/useAppDispatch';
import DisclaimerText from '../../../../common/disclaimer-text/DisclaimerText';
import { LibraryListRow } from './LibraryListRow';
import { selectUser } from '../../../../slices/user/userSlice';

const LibraryList = ({ libraryItems }: LibraryListProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [copyModalOpen, setCopyModalOpen] = useState<LibraryItem | null>(null);
  const company = useAppSelector(selectAllCompanies)[0];
  const navigate = useNavigate();
  const documentUploads = useAppSelector(selectAllDocuments);
  const disclaimerAccepted = useAppSelector(selectDisclaimerChecked);
  const disclaimerStatus = useAppSelector((state) => state.disclaimerInfo.status);
  const freePlan = !company?.stripe_product_id && !company?.plan_settings.stripe_product_id;
  const currentUser = useAppSelector(selectUser);
  const {
    handleS3DownloadLink,
    handleLibraryItemCopy,
    planUpgradingMessage,
    setPlanUpgradingMessage,
    navigateToTemplatePage,
    disclaimerMessage,
    handleTemplateCopy,
    setDisclaimerMessage,
    libraryItemForGenerator,
    setLibraryItemForGenerator,
  } = useLibraryFunctions();
  const {
    setModalOpen: setFileTypeModalOpen,
    modalOpen: fileTypeModalOpen,
    handleCloseModal: handleCloseFileTypeModal,
    libraryItem: libraryItemForDownloading,
    setLibraryItem: setLibraryItemForDownloading,
    setFileName: setFileNameForDownloading,
    fileName: fileNameForDownloading,
    fileType: fileTypeForDownloading,
    setFileType: setFileTypeForDownloading,
    handleTemplateDownload,
    pendingStatus: downloadPendingStatus,
  } = useFileTypeDownload();

  const {
    modalOpen: deleteLibraryItemModalOpen,
    setModalOpen: setDeleteLibraryItemModalOpen,
    handleDeleteDocumentTemplate,
    libraryItemToDelete,
    setLibraryItemToDelete,
    pendingStatus: deletingLibraryItemPending,
  } = useDeleteLibraryItemMessageModal();

  const handleNavigate = (item: LibraryItem) => {
    const actionAvailableFree = 'is_free' in item && item.is_free;
    if (item.download_link) {
      return false;
    } else {
      if (freePlan) {
        if (actionAvailableFree) {
          navigateToTemplatePage('region' in item ? item.region : 'company-templates', item.id);
        } else {
          setPlanUpgradingMessage(true);
        }
      } else {
        navigateToTemplatePage('region' in item ? item.region : 'company-templates', item.id);
      }
    }
  };

  const renderTableHeader = () => (
    <TableHead>
      <TableRow className={styles.header_row}>
        <TableCell className={styles.header_cell}>
          <Typography variant='body2' className={styles.header_text}>
            {t('libraryTable.name')}
          </Typography>
        </TableCell>
        <TableCell className={styles.header_cell}>
          <Typography variant='body2' className={styles.header_text}>
            {t('libraryTable.profiles')}
          </Typography>
        </TableCell>
        <TableCell className={styles.header_cell}>
          <Typography variant='body2' className={styles.header_text}>
            {t('libraryTable.workflow')}
          </Typography>
        </TableCell>
        <TableCell className={styles.header_cell}>
          <Typography variant='body2' className={styles.header_text}>
            {t('libraryTable.documentType')}
          </Typography>
        </TableCell>
        <TableCell className={styles.header_cell}>
          <Typography variant='body2' className={styles.header_text}>
            {t('libraryTable.dateCreatedModified')}
          </Typography>
        </TableCell>
        <TableCell className={`${styles.header_cell} ${styles.actions_header}`}>
          <Typography variant='body2' className={styles.header_text}>
            {t('libraryTable.actions')}
          </Typography>
        </TableCell>
      </TableRow>
    </TableHead>
  );

  const CopyModal: React.FC<{ item: LibraryItem }> = ({ item }) => {
    const handleCopying = (newname: string) => {
      const data = {
        name: newname,
        region: item.region,
        dt_workflows: item.dl_workflows,
        dt_types: item.dl_types,
        user: currentUser.id,
      };

      try {
        handleLibraryItemCopy(data, item.id); // Wait for the copy operation to complete
        setCopyModalOpen(null); // Close the modal after the operation finishes
      } catch (error) {
        console.error('Error copying library item:', error);
      }
    };
    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const formData = new FormData(e.currentTarget);
      const newTemplateName = formData.get('newTemplateName') as string;
      if ('region' in item) {
        handleCopying(newTemplateName);
      } else {
        const updatedItem = { ...item, name: newTemplateName };
        try {
          handleTemplateCopy(updatedItem, currentUser.id);
          setCopyModalOpen(null);
        } catch (error) {
          console.error('Error copying template:', error);
        }
      }
    };
    return (
      <Modal
        open={copyModalOpen?.id === item.id}
        className={styles.copymodal}
        onClose={() => setCopyModalOpen(null)}
      >
        <Grid container sx={{ boxShadow: 'none', border: 'none', outline: 'none' }}>
          <Grid item xs={12} sm={11} md={11} lg={11}>
            <Card>
              <CardContent sx={{ paddingBottom: '16px !important' }}>
                <Typography variant={'h6'} gutterBottom>
                  Copy to Own library
                </Typography>

                <Typography>Please change the template name if necessary.</Typography>
                <form onSubmit={handleSubmit}>
                  <Box sx={{ maxHeight: '500px', overflow: 'auto' }}>
                    <Input
                      name='newTemplateName'
                      placeholder={item.name ? item.name : t('templatePage.headerDefault')}
                      defaultValue={`${
                        item.name ? item.name : t('templatePage.headerDefault')
                      } - ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}`}
                      className={styles.input}
                      fullWidth
                      required
                    />
                  </Box>

                  <Grid container justifyContent='flex-end' gap={'8px'} mt={2}>
                    <Button
                      variant='contained'
                      onClick={() => setCopyModalOpen(null)}
                      className={'btn-default btn-action-basic btn-cancel'}
                    >
                      Go back
                    </Button>

                    <Button
                      variant='contained'
                      type='submit'
                      className='btn-default btn-action-basic'
                    >
                      Confirm
                    </Button>
                  </Grid>
                </form>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Modal>
    );
  };

  const renderActionButtons = (item: LibraryItem) => {
    const actionAvailableFree = 'is_free' in item && item.is_free;
    const templateMoreActions = [
      {
        name: t('buttons.view'),
        onClick: () => {
          handleNavigate(item);
        },
      },
      { name: t('buttons.copyDocument'), onClick: () => setCopyModalOpen(item) },
      {
        name: t('buttons.edit'),
        onClick: () => {
          handleNavigate(item);
        },
      },
      {
        name: t('buttons.delete'),
        onClick: () => {
          setLibraryItemToDelete(item);
          setDeleteLibraryItemModalOpen(true);
        },
      },
    ];

    const handleGenerateContract = () => {
      const actionAvailableFree = 'is_free' in item && item.is_free;
      if ((freePlan && actionAvailableFree) || !freePlan || !('region' in item)) {
        if (disclaimerAccepted) {
          navigate(
            `${ROUTES.DOCUMENT_LIBRARY}/${item.region ? item.region : 'company-templates'}/${
              item.id
            }?generateDocument=true`
          );
        } else {
          setLibraryItemForGenerator(item);
          setDisclaimerMessage(true);
        }
      } else if (freePlan && !actionAvailableFree) {
        setPlanUpgradingMessage(true);
      }
    };

    const handleDownloading = () => {
      if (!item.download_link) {
        setFileNameForDownloading(
          'name' in item
            ? item.name
            : documentUploads
                ?.find((el) => el.id === item.document_upload)
                ?.name?.replace('.pdf', '')
                .replace('.docx', '')
                .replace('.doc', '')
        );
        setLibraryItemForDownloading(item);
        setFileTypeModalOpen(true);
      } else {
        handleS3DownloadLink(item.id, 'name' in item ? item.name : '');
      }
    };

    const handleTemplateCopying = (item: LibraryItem) => {
      handleTemplateCopy(item, currentUser.id);
    };

    return (
      <Box className={styles.actions_container}>
        {!item.download_link && (
          <Tooltip title={t('buttons.generateContract')} placement='top' arrow>
            <IconButton
              size='small'
              onClick={() => handleGenerateContract()}
              className={styles.action_button}
            >
              <ContractGeneratorIcon />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={t('buttons.download')} placement='top' arrow>
          <IconButton
            size='small'
            onClick={() => {
              if (freePlan || !item.region) {
                if (actionAvailableFree || !item.region) {
                  handleDownloading();
                } else {
                  setPlanUpgradingMessage(true);
                }
              } else {
                handleDownloading();
              }
            }}
            className={styles.action_button}
          >
            <FileSaveIcon />
          </IconButton>
        </Tooltip>
        {!item.download_link && (
          <Tooltip title={t('buttons.copyTemplate')} placement='top' arrow>
            <IconButton
              size='small'
              onClick={() => {
                if (freePlan || !item.region) {
                  if (actionAvailableFree || !item.region) {
                    setCopyModalOpen(item);
                  } else {
                    setPlanUpgradingMessage(true);
                  }
                } else {
                  setCopyModalOpen(item);
                }
              }}
              className={styles.action_button}
            >
              <ContentCopyIcon />
            </IconButton>
          </Tooltip>
        )}
        {!item.category && (
          <MenuBlock options={templateMoreActions} moreIcon={<VerticalDotsIcon />} />
        )}
      </Box>
    );
  };

  return (
    <>
      <TableContainer
        component={Paper}
        className={styles.table_container}
        sx={{ boxShadow: 'none' }}
      >
        <Table>
          {renderTableHeader()}
          <TableBody>
            {libraryItems.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <p className={styles.empty_message}>
                    {t('emptyStateMessages.emptyLibraryMessage')}
                  </p>
                </TableCell>
              </TableRow>
            )}
            {libraryItems.map((item) => {
              return (
                <React.Fragment key={`${item.id}-${uuidv4()}`}>
                  <LibraryListRow item={item} renderActionButtons={renderActionButtons} />
                  <CopyModal item={item} />
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {planUpgradingMessage && (
        <MessageModal
          open={planUpgradingMessage}
          modalMessage={t('messages.considerStartingPlanLibrary')}
          actionButtonText={t('buttons.ok')}
          actionButtonFn={() => navigate(ROUTES.PRICING)}
          goBackButtonText={t('buttons.later')}
          onClose={() => {
            setPlanUpgradingMessage(false);
          }}
        />
      )}
      {fileTypeModalOpen && libraryItemForDownloading && (
        <FileTypeDownloadModal
          onClose={() => {
            handleCloseFileTypeModal();
          }}
          open={fileTypeModalOpen}
          fileName={fileNameForDownloading}
          fileType={fileTypeForDownloading}
          handleFileTypeChange={setFileTypeForDownloading}
          handleFileNameChange={setFileNameForDownloading}
          libraryItem={libraryItemForDownloading}
          handleDownloadLibraryHTML={() => {
            handleTemplateDownload(
              libraryItemForDownloading.id,
              fileTypeForDownloading,
              fileNameForDownloading
            );
          }}
        />
      )}
      {deleteLibraryItemModalOpen && libraryItemToDelete && (
        <MessageModal
          open={deleteLibraryItemModalOpen}
          modalTitle={t('dialogTitles.deleteTemplate')}
          modalMessage={t('messages.deleteTemplate')}
          actionButtonText={t('buttons.deleteTemplate')}
          actionButtonFn={handleDeleteDocumentTemplate}
          goBackButtonText={t('buttons.goBack')}
          document={libraryItemToDelete}
          onClose={() => {
            setLibraryItemToDelete(null);
            setDeleteLibraryItemModalOpen(false);
          }}
        />
      )}
      {disclaimerMessage && (
        <MessageModal
          open={disclaimerMessage}
          modalTitle={t('headers.documentTerms')}
          modalContent={<DisclaimerText />}
          actionButtonText={t('buttons.accept')}
          headerVariant={'h5'}
          actionButtonFn={() => {
            dispatch(acceptDisclaimer())
              .unwrap()
              .then(() => {
                setDisclaimerMessage(false);
                navigate(
                  `${ROUTES.DOCUMENT_LIBRARY}/${
                    libraryItemForGenerator.region
                      ? libraryItemForGenerator.region
                      : 'company-templates'
                  }/${libraryItemForGenerator.id}?generateDocument=true`
                );
                //setGeneratorOpened(true);
              });
          }}
          goBackButtonText={t('buttons.goBack')}
          onClose={() => {
            setDisclaimerMessage(false);
            setLibraryItemForGenerator(null);
          }}
        />
      )}
      {(downloadPendingStatus ||
        deletingLibraryItemPending ||
        disclaimerStatus === 'loadingAccepting') && <ProgressOverlay />}
    </>
  );
};

export default LibraryList;
